import {api} from "../shared"
import {ApiObject} from "./api"
import {ApiObjectFields, EOFields, ApiObjectCalcField, EOFieldsWithParent,
  Guid, ItemCode, WarehouseCode,
} from "./types"
import {TypeMap, updateableTM, assertTM, apiToNotesFieldName,
  UpdateSignature, copyFilterEOValueToUpdateValue,
  GuidTypeMap, NullableStringTypeMap, StringTypeMap, NumberTypeMap, ISODateTypeMap,
  NumericBooleanTypeMap, ItemCodeTypeMap, WarehouseCodeTypeMap,
  NonNegIntCalcTypeMap, DateCalcTypeMap, YesNoCalcTypeMap, PoLCalcTypeMap,
  getCalcData,
} from "./generaltypemaps"
import {getSalesOrderEOURL} from "../links"
import Enum from "enum-typescript"
import {ISODate} from "../date"
import {TSAssertType, AddPrefix, RemovePrefix} from "../tsmagic"
import utils from "../utils"

const WRITE_LOG = false

export const WARN_IF_VALUE_ABOVE = 1e4  // lines above this value generate warning

export class SOStatus extends Enum<number> {
  static OPEN = SOStatus.createEnum(12)
  static PARTIAL = SOStatus.createEnum(20)
  static CLOSED = SOStatus.createEnum(21)
  static CANCELLED = SOStatus.createEnum(45)
}


export const SOStatusTypeMap: TypeMap<SOStatus> = copyFilterEOValueToUpdateValue({
  fromEO: (data: number) => SOStatus.valueOf(assertTM(
    d => SOStatus.valueOf(d) !== undefined, data)),
  toEOFilterValue: (data: SOStatus) => data.value.toString(),
})


export const SalesOrderFields = {
  orderID: GuidTypeMap,
  description: NullableStringTypeMap,
  remarks: NullableStringTypeMap,
  deliverToName: StringTypeMap,
  orderNumber: NumberTypeMap,
  orderedByName: StringTypeMap,
  status: SOStatusTypeMap,
  orderDate: ISODateTypeMap,
  deliveryDate: ISODateTypeMap,
  warehouseCode: WarehouseCodeTypeMap,
  warehouseDescription: StringTypeMap,
}

const PrioDoc = `
The priority for this order. A higher number means a higher priority.
Default priority is <code>50</code>, so <code>60</code> would be a
higher priority, and <code>40</code> a lower priority than normal.
It is advised to use <code>>@@priority: 100</code> for orders
that absolutely need to be shipped on the requested day (and never
use a priority larger than 100).

Note that if this value is not set on the Sale Order, but it is
set on the Customer, then the order gets the priority value of
the customer.

<example>100</example>
`

const FicOrderDateDoc = `
Normally the sales order tool deals with orders by highest priority,
and earliest order date. In certain cases, one would like to move
an order manually backwards or forwards in this line.
If this field is set, the order is considered to have this order date,
when deciding in which order to process the items.

<example>2021-06-30</example>
`

const PartShipMaxDelaysDoc = `
If an order has multiple lines which have stock available on
different dates, a decision should be made whether to do a partial
shipment. This parameter determines how many days should be "won"
before a partial shipment is done.

For instance, if there are two order lines for which stock
becomes available with 7 days apart, and this value
is <code>7</code> or larger, the order is not split into partial
shipments; if the value is <code>6</code> or lower, the order
is split into partial shipments.

If this value is not set, order gets the default value for
the customer.

<example>10</example>
`

const WarnIfDelayedDoc = `
Issue a warning in the sales order tool if this order gets delayed,
meaning it will get shipped later than requested.
Default is <code>no</code>.
Normally the SO tool moves an order to the first moment when there
is enough stock to ship it.
In some cases however (e.g. with time critical orders), if there is
not enough stock, one would like to do something else (contact the
customer, lower order quantity, move some other orders around).
In these cases, set <code>>@@warn-if-delayed: yes</code>,
and you will see a warning if this order is moved backwards.
`


export const SalesOrderCalcFields = {
  priority: {typemap: NonNegIntCalcTypeMap, default: 50, documentation: PrioDoc},
  // some weird typescript bug requires to set null as type null explicitly
  fictionalOrderDate: {typemap: DateCalcTypeMap, default: null as null,
    documentation: FicOrderDateDoc},
  partialShipmentMaxDelayDays: {typemap: NonNegIntCalcTypeMap, default: 7,
    documentation: PartShipMaxDelaysDoc},
  warnIfDelayed: {typemap: YesNoCalcTypeMap, default: false,
    documentation: WarnIfDelayedDoc},
} as const

export const SalesOrderLineFields = {
  iD: GuidTypeMap,
  amountDC: NumberTypeMap,
  description: NullableStringTypeMap,
  itemCode: ItemCodeTypeMap,
  itemDescription: StringTypeMap,
  notes: NullableStringTypeMap,
  deliveryStatus: SOStatusTypeMap,
  quantity: NumberTypeMap,
  quantityDelivered: NumberTypeMap,
  useDropShipment: NumericBooleanTypeMap,
  deliveryDate: updateableTM(ISODateTypeMap),
  lineNumber: NumberTypeMap,
}

const DSDoc = `
See description for <code>drop-shipment</code> for Purchase Order Line.

<example>PO:23543(3)</example>
`

const WarnIf2LDoc = `
Normally if a line has a value of more than
\u20AC${WARN_IF_VALUE_ABOVE.toFixed(2)}, a warning is raised that one should
probably split the line into multiple smaller lines.
By adding <code>>@@warn-if-too-large: no</code>, this warning
is suppressed.
`
const TPSDoc = `
Normally a line will trigger a partial shipment if it's available
before other lines (depending on the value
of <code>partial-shipment-max-delay-days</code>.
For some items it may not make sense to trigger a partial shipment
(e.g. if someone orders 10 phones and 10 screen protectors, it
probably does not make sense to send the 10 screen protectors
today, if the phones will only be available in 2 months time;
even though you might want to send the phones today if the screen
protectors are only available in 2 months).
In this case, one can add
the <code>>@@trigger-partial-shipment: no</code> to the 
order line for the screen protectors. Default value
is <code>yes</code>.
`



export const SalesOrderLineCalcFields = {
  dropShipment: {typemap: PoLCalcTypeMap, default: null as null,
    documentation: DSDoc},
  warnIfTooLarge: updateableTM({typemap: YesNoCalcTypeMap, default: true,
    documentation: WarnIf2LDoc}),
  triggerPartialShipment: {typemap: YesNoCalcTypeMap, default: true,
    documentation: TPSDoc},
}


export class SalesOrder extends ApiObject
    implements ApiObjectFields<typeof SalesOrderFields>,
    ApiObjectCalcField<typeof SalesOrderCalcFields>
    {
  readonly orderID: Guid
  readonly description: string | null
  readonly remarks: string | null
  readonly deliverToName: string
  readonly orderNumber: number
  readonly orderedByName: string
  readonly orderDate: ISODate
  readonly deliveryDate: ISODate
  readonly status: SOStatus
  readonly warehouseCode: WarehouseCode
  readonly warehouseDescription: string

  readonly salesOrderLines: SalesOrderLine[]

  readonly $calc: ApiObjectCalcField<typeof SalesOrderCalcFields>["$calc"]

  constructor(props: EOFields<typeof SalesOrder>) {
    super({})
    this.orderID = SalesOrder.FIELDS.orderID.fromEO(props.OrderID)
    this.description = SalesOrder.FIELDS.description.fromEO(props.Description)
    this.remarks = SalesOrder.FIELDS.remarks.fromEO(props.Remarks)
    this.deliverToName =
      SalesOrder.FIELDS.deliverToName.fromEO(props.DeliverToName)
    this.orderNumber = SalesOrder.FIELDS.orderNumber.fromEO(props.OrderNumber)
    this.orderDate = SalesOrder.FIELDS.orderDate.fromEO(props.OrderDate)
    this.deliveryDate = SalesOrder.FIELDS.deliveryDate.fromEO(props.DeliveryDate)
    this.orderedByName = SalesOrder.FIELDS.orderedByName.fromEO(props.OrderedByName)
    this.status = SalesOrder.FIELDS.status.fromEO(props.Status)
    this.warehouseCode =
      SalesOrder.FIELDS.warehouseCode.fromEO(props.WarehouseCode)
    this.warehouseDescription =
      SalesOrder.FIELDS.warehouseDescription.fromEO(props.WarehouseDescription)

    this.salesOrderLines = props.SalesOrderLines.map(
      eoSOL => new SalesOrderLine({...eoSOL, Parent: this})).sort(
      (sol1, sol2) => sol1.lineNumber - sol2.lineNumber)

    this.$calc = getCalcData(this.remarks, SalesOrderCalcFields)
  }

    // I would *love* to use the bunk version, however that has no status or
    // quantityDelivered in the lines. So I have no idea if the order still needs
    // to be fulfilled.
  static get URL(): string {
    return "salesorder/SalesOrders"
  }

  static get FIELDS(): typeof SalesOrderFields {
    return SalesOrderFields
  }

  static get EXPANDFIELDS(): typeof SalesOrderExpandFields {
    return SalesOrderExpandFields
  }

  getExactOnlineUrl(division: number): URL {
    return getSalesOrderEOURL(division, this.orderID)
  }

  getId(): Guid {
    return this.orderID
  }
}

export class SalesOrderLine extends ApiObject
    implements ApiObjectFields<typeof SalesOrderLineFields>
    , ApiObjectCalcField<typeof SalesOrderLineCalcFields> {
  readonly parent: SalesOrder

  readonly iD: Guid
  readonly amountDC: number
  readonly description: string | null
  readonly itemCode: ItemCode
  readonly itemDescription: string
  readonly notes: string | null
  readonly quantityDelivered: number
  readonly quantity: number
  readonly deliveryDate: ISODate
  readonly deliveryStatus: SOStatus
  readonly lineNumber: number
  readonly useDropShipment: 0 | 1

  readonly $calc: ApiObjectCalcField<typeof SalesOrderLineCalcFields>["$calc"]

  constructor(props: EOFieldsWithParent<typeof SalesOrderLine>) {
    super({})
    this.iD = SalesOrderLine.FIELDS.iD.fromEO(props.ID)
    this.amountDC = SalesOrderLine.FIELDS.amountDC.fromEO(props.AmountDC)
    this.description =
      SalesOrderLine.FIELDS.description.fromEO(props.Description)
    this.itemCode =
      SalesOrderLine.FIELDS.itemCode.fromEO(props.ItemCode)
    this.itemDescription =
      SalesOrderLine.FIELDS.itemDescription.fromEO(props.ItemDescription)
    this.notes = SalesOrderLine.FIELDS.notes.fromEO(props.Notes)
    this.quantityDelivered =
      SalesOrderLine.FIELDS.quantityDelivered.fromEO(props.QuantityDelivered)
    this.quantity = SalesOrderLine.FIELDS.quantity.fromEO(props.Quantity)
    this.deliveryDate =
      SalesOrderLine.FIELDS.deliveryDate.fromEO(props.DeliveryDate)
    this.lineNumber = SalesOrderLine.FIELDS.lineNumber.fromEO(props.LineNumber)
    this.deliveryStatus =
      SalesOrderLine.FIELDS.deliveryStatus.fromEO(props.DeliveryStatus)
    this.useDropShipment =
      SalesOrderLine.FIELDS.useDropShipment.fromEO(props.UseDropShipment)

    this.parent = props.Parent

    this.$calc = getCalcData(this.notes, SalesOrderLineCalcFields)
  }

  static get FIELDS(): typeof SalesOrderLineFields {
    return SalesOrderLineFields
  }

  static get URL(): string {
    return "salesorder/SalesOrderLines"
  }

  get SINGLE_ITEM_URL(): string {
    return `${SalesOrderLine.URL}(${
      SalesOrderLine.FIELDS.iD.toEOUpdateValue(this.iD)})`
  }

  async update(
    updates: UpdateSignature<
      typeof SalesOrderLineFields, typeof SalesOrderLineCalcFields>,
    division: number,
    trigger: string,
    tick: null | (() => void)
  ): Promise<boolean> {
    type Change<key extends keyof typeof updates> = {
      name: key extends AddPrefix<string, "$calc.">
      ? AddPrefix<Lowercase<string>, ">@@"> : key,
      from: string,
      to: string,
    }
    const solFromDatabase = await this.getCopyFromDatabase(api, division)
    if (this.notes !== solFromDatabase.notes) {
      throw new Error("Looks like sales order line changed in Exact Online "
        + "since you last loaded it. Please reload your data")
    }

    const changes: Change<keyof typeof updates>[] = []
    const EOUpdates = new Map<Capitalize<keyof typeof SalesOrderLineFields>,
    boolean | null | number | string>(
      Object.entries(updates).filter(([key]) => !key.startsWith("$calc."))
      .map(([key, newValue]) => {
        TSAssertType<keyof typeof SalesOrderLineFields & keyof typeof updates>(key)
        const eoKey = key.slice(0, 1).toUpperCase() + key.slice(1)
        const eoValue = SalesOrderLine.FIELDS[key].toEOUpdateValue(newValue as any)
        const oldEOValue = SalesOrderLine.FIELDS[key].toEOUpdateValue(
          this[key] as any)
        const oldEOValueFromDatabase = SalesOrderLine.FIELDS[key].toEOUpdateValue(
          solFromDatabase[key] as any)
        if (oldEOValue !== oldEOValueFromDatabase) {
          throw new Error("Looks like sales order line changed in Exact Online "
            + "since you last loaded it. Please reload your data")
        }
        if (eoValue === oldEOValue) {
          return null
        }
        changes.push({name: key, from: oldEOValue, to: eoValue})
        return [eoKey, eoValue] as [
          Capitalize<typeof key>, boolean | string | null | number]
      }).filter(utils.notNullOrUndefined)
    )
    const noteLines = (this.notes ?? "").split("\n")
    Object.entries(updates).filter(([key]) => key.startsWith("$calc."))
    .forEach(([key, newValue]) => {
      TSAssertType<keyof typeof updates>(key)
      const calcKey = key.slice("$calc.".length) as (
        RemovePrefix<keyof typeof updates, "$calc.">)
      const eoValue = SalesOrderLineCalcFields[calcKey].typemap.unparse(
        newValue as any)
      const oldEOValue = this.$calc[calcKey] === null ? "<null>" :
        SalesOrderLineCalcFields[calcKey].typemap.unparse(
          this.$calc[calcKey] as any)
      if (eoValue === oldEOValue) {
        return
      }
      const calcNotesKey = apiToNotesFieldName(calcKey)
      changes.push({name: `>@@${calcNotesKey}`, from: oldEOValue, to: eoValue})
      const lineIndex =
        noteLines.findIndex(line => line.startsWith(`>@@${calcNotesKey}:`))
      const newLine = `>@@${calcNotesKey}: ${eoValue}`
      if (lineIndex === -1) {
        noteLines.push(newLine)
      } else {
        noteLines.splice(lineIndex, 1, newLine)
      }
    })
    const me = await api.getMe()
    const isoNow = new Date().toISOString().replace(/:\d\d\.\d\d\dZ/, "Z")
    const newNotes = [
      ...noteLines.filter(line => line.startsWith(">@@")),
      "",  // add empty line
      ...noteLines.filter(line => !line.startsWith(">@@")).filter(
        (line, index) => line !== "" || index !== 0), // remove first line if empty
      ...(WRITE_LOG ? changes.map(change =>
        `@${isoNow}: ${change.name} ${change.from} => ${change.to}`
      + ` (${trigger} - ${me.userName})`) : [])
    ].join("\n")
    if (newNotes.trim() !== (this.notes ?? "").trim()) {
      EOUpdates.set("Notes", newNotes)
    }

    await this.doUpdate(api, division, Object.fromEntries(EOUpdates.entries()))

    // update this
    EOUpdates.forEach((value, key) => {
      const objectKey = key.slice(0, 1).toLowerCase() + key.slice(1) as (
        keyof typeof SalesOrderLineFields)
      ;(this[objectKey] as any) = (
        SalesOrderLine.FIELDS[objectKey].fromEO as any)(value)
    })
    ;(this.$calc as any) = getCalcData(this.notes, SalesOrderLineCalcFields)

    if (tick) {
      tick()
    }
    return true
  }

  getOrderNumberLineNumber(): string {
    /**
     * This ought to be a unique ID -- however in rare cases linenumbers are reused
     */
    return `${this.parent.orderNumber}(${this.lineNumber})`
  }

  getDelayDays(lineDeliveryDate?: ISODate): number {
    /**
     * Returns how many days after requested this order was/will be shipped
     **/
    return this.parent.deliveryDate.getDaysUntil(
      lineDeliveryDate ?? this.deliveryDate)
  }
}

const SalesOrderExpandFields = {
  salesOrderLines: SalesOrderLine
} as const
